/**
 * Reusable Badge — supports variants, status mapping, and children.
 *
 * Usage:
 *   <Badge variant="success" label="Active" />
 *   <Badge status="pending" />
 *   <Badge variant="danger">Out of Stock</Badge>
 */

const VARIANT_STYLES = {
  info: 'bg-blue-50 text-blue-700 border-blue-100',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  warning: 'bg-amber-50 text-amber-700 border-amber-100',
  danger: 'bg-red-50 text-red-700 border-red-100',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-100',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  teal: 'bg-teal-50 text-teal-700 border-teal-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  orange: 'bg-gray-50 text-gray-700 border-gray-100',
  blue: 'bg-blue-50 text-blue-700 border-blue-100',
};

const STATUS_MAP = {
  pending: 'warning',
  approved: 'success',
  owner_approved: 'success',
  awaiting_owner_approval: 'purple',
  rejected: 'danger',
  cancelled: 'neutral',
  dispatched: 'info',
  delivered: 'success',
  partially_delivered: 'warning',
  fulfilled: 'success',
  in_stock: 'success',
  out_of_stock: 'danger',
  low_stock: 'warning',
  SD: 'orange',
  tower: 'blue',
};

export default function Badge({
  variant,
  status,
  label,
  children,
  dot = false,
  className = ''
}) {
  const resolvedVariant = variant || (status ? STATUS_MAP[status.toLowerCase()] : 'neutral') || 'neutral';
  const styles = VARIANT_STYLES[resolvedVariant] || (resolvedVariant.includes('bg-') ? resolvedVariant : VARIANT_STYLES.neutral);
  const displayLabel = label || children || (status ? status.replace(/_/g, ' ').toUpperCase() : '');

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${styles} ${className}`}>
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      {displayLabel}
    </span>
  );
}
//updated by Halimunnisa on 10-06-2026 - Created reusable Badge component with variant and status mapping for consistent UI across lead and project statuses in SD Projects.
const LEAD_STATUS_MAP = {
  // ── Standard CRM Lead Statuses ────────────────────────────────
  new:                      { label: 'New',                       variant: 'blue'    },
  contacted:                { label: 'Contacted',                 variant: 'purple'  },
  interested:               { label: 'Interested',                variant: 'amber'   },
  site_visit_requested:     { label: 'Site Visit',                variant: 'orange'  },
  initial_quotation:        { label: 'Initial Quotation',         variant: 'cyan'    },
  client_approval:          { label: 'Client Approval',           variant: 'sky'     },
  quotation_sent:           { label: 'Quotation Sent',            variant: 'indigo'  },
  negotiation:              { label: 'Negotiation',               variant: 'purple'  },
  converted:                { label: 'Converted',                 variant: 'success' },
  lost:                     { label: 'Lost',                      variant: 'danger'  },

  // ── Project Pipeline Stages (SD Projects Lead Status) ──────
  cust_acceptance:          { label: 'Client Accepted',           variant: 'indigo'  },
  customer_acceptance:      { label: 'Client Accepted',           variant: 'indigo'  },
  design:                   { label: 'Design',                    variant: 'purple'  },
  design_approval:          { label: 'Design Approval',           variant: 'purple'  },
  final_quotation:          { label: 'Final Quotation',           variant: 'orange'  },
  quotation_approval:       { label: 'Final Quotation Approval',  variant: 'success' },
  documents:                { label: 'Sign Off',                  variant: 'neutral' },
  project_started:          { label: 'Started',                   variant: 'teal'    },
};

const PROJECT_STATUS_MAP = {
  planning: { label: 'Planning', variant: 'neutral' },
  materials_requested: { label: 'Materials Req.', variant: 'blue' },
  materials_delivered: { label: 'Mat. Delivered', variant: 'indigo' },
  work_started: { label: 'Work Started', variant: 'warning' },
  installation_in_progress: { label: 'In Progress', variant: 'orange' },
  completed: { label: 'Completed', variant: 'success' },
};

export function LeadStatusBadge({ status, className = '' }) {
  const config = LEAD_STATUS_MAP[status?.toLowerCase()] || { label: status, variant: 'neutral' };
  return (
    <Badge
      variant={config.variant}
      label={config.label}
      className={className}
    />
  );
}

export function ProjectStatusBadge({ status, className = '' }) {
  const config = PROJECT_STATUS_MAP[status?.toLowerCase()] || { label: status, variant: 'neutral' };
  return (
    <Badge
      variant={config.variant}
      label={config.label}
      className={className}
    />
  );
}




